// =============================================================================
// Colyseus room schema shapes (mirror of `packages/shared/src/rooms/*.ts`).
//
// Colyseus delivers these as proxied schema instances; the shapes below are
// for typing reads only. Mutations come from the server.
//
// See docs/COLYSEUS.md.
// =============================================================================

import type { Element, Realm, SkillTarget, StatName } from "./static-data";

// ---------- world_zone ----------

export interface ZonePlayer {
  characterId: string;
  name: string;
  level: number;
  title: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Heading: 0=down,1=left,2=right,3=up (server convention). */
  dir: number;
  anim: string; // "idle" | "walk" | ...
  ts: number;
  companionSpeciesId: string | null;
  mountSpeciesId: string | null;
}

export interface ZoneMob {
  id: string;
  speciesId: string;
  level: number;
  behavior: "passive" | "neutral" | "aggressive";
  x: number;
  y: number;
  anim: string;
}

export interface ZoneBossLite {
  spawnId: string;
  bossId: string;
  speciesId: string;
  level: number;
  x: number;
  y: number;
  /** ms epoch. */
  expiresAt: number;
}

export interface ZoneNpcLite {
  id: string;
  name: string;
  role: string;
  x: number;
  y: number;
}

export interface ZoneExitLite {
  label: string;
  toZone: string;
  x: number;
  y: number;
  interactRadius: number;
  requiredLevel?: number;
}

/** Colyseus state schemas appear as plain objects when serialized; treat as
 *  read-only and listen via getStateCallbacks(room). */
export interface WorldZoneState {
  zoneId: string;
  zoneName: string;
  realm: Realm;
  width: number;
  height: number;
  tileSize: number;
  shardIndex: number;
  /** Maps over collections — iterated via $.players.onAdd / .onChange. */
  players: Map<string, ZonePlayer>;
  mobs: Map<string, ZoneMob>;
  bosses: ZoneBossLite[];
  npcs: ZoneNpcLite[];
  exits: ZoneExitLite[];
}

// ---------- world_zone messages ----------

export interface WorldEncounterEvent {
  encounterId: string;
  species: string;
  level: number;
  behavior: ZoneMob["behavior"];
  forced: boolean;
  mobId?: string;
}

export interface WorldNpcInteractEvent {
  npcId: string;
  name: string;
  role: string;
  shopId?: string;
  questIds?: string[];
  dialogue?: Array<{ id: string; lines: string[] }>;
}

export interface WorldCombatInteractEvent {
  kind: "mob" | "boss";
  id: string;
}

export interface WorldTeleportEvent {
  toZone: string;
  x: number;
  y: number;
  label: string;
}

export interface WorldEmoteEvent {
  characterId: string;
  kind: string;
  ts: number;
}

export type WorldInteractError =
  | "out_of_range"
  | "level_too_low"
  | "npc_not_found"
  | "exit_not_found";

// ---------- global ----------

export interface GlobalChatMessage {
  channel: "global" | "whisper" | "party";
  fromName: string;
  body: string;
  ts: number;
}

export interface GlobalState {
  presence: Map<string, { characterId: string; name: string; level: number }>;
  recentChat: GlobalChatMessage[]; // last 20
  onlineCount: number;
}

export type ChatChannel = "global" | "whisper" | "party";

export interface ChatSendPayload {
  channel: ChatChannel;
  body: string;
  to?: string;
}

export type ChatErrorReason =
  | "rate_limited"
  | "muted"
  | "target_offline"
  | "whisper_target_required"
  | "not_in_party"
  | "party_missing"
  | "profanity"
  | "link_blocked"
  | "flood"
  | "spam"
  | "too_long"
  | "empty"
  | "blocked";

export interface ChatErrorEvent {
  reason: ChatErrorReason;
  matched?: string;
  autoMuteSeconds?: number;
  until?: string;
}

export type NotificationKind =
  | "friend_request"
  | "friend_accepted"
  | "mail_received"
  | "achievement_unlocked"
  | "title_unlocked"
  | "trade_invite"
  | "party_invite"
  | "guild_invite"
  | "tamer_level_up";

export interface NotificationEvent {
  kind: NotificationKind;
  payload: Record<string, unknown>;
  ts: number;
}

// ---------- party ----------

export interface PartyMember {
  characterId: string;
  name: string;
  level: number;
  ready: boolean;
  status: "idle" | "in_battle" | "offline";
  joinedAt: number;
}

export interface PartyState {
  partyId: string;
  leaderId: string;
  members: Map<string, PartyMember>;
  currentBattleRoomId: string;
}

// ---------- battle ----------

export interface CombatantStatus {
  name: string;
  remaining: number;
  potency: number;
}

export interface CombatantSchema {
  id: string;
  side: "player" | "enemy";
  speciesId: string;
  speciesName: string;
  element: Element;
  level: number;
  hp: number;
  maxHp: number;
  atk: number;
  def: number;
  spd: number;
  sp_atk: number;
  sp_def: number;
  fainted: boolean;
  stages: Map<StatName, number>;
  statuses: CombatantStatus[];
  skills: string[];
  pp: Map<string, number>;
}

export interface BattleSideSchema {
  characterId: string;
  activeCombatantId: string;
  bench: string[];
  handCount: number;
  deckCount: number;
  mana: number;
  maxMana: number;
  timeoutStrikes: number;
}

export interface BattleLogLine {
  ts: number;
  text: string;
}

export type BattlePhase = "input" | "resolve" | "victory" | "defeat" | "finished";
export type BattleMode = "pve" | "pvp_casual" | "pvp_ranked";

export interface BattleSchema {
  battleId: string;
  mode: BattleMode;
  phase: BattlePhase;
  turn: number;
  waveIndex: number;
  encounterId: string;
  /** ms epoch. */
  turnDeadline: number;
  combatants: Map<string, CombatantSchema>;
  player: BattleSideSchema;
  enemy: BattleSideSchema;
  log: BattleLogLine[];
}

// ---------- battle messages ----------

export interface BattleHandCard {
  cardInstanceId: string;
  templateId: string;
  name: string;
  cost: number;
  target: SkillTarget;
}

export interface BattleHandPayload {
  side: "player";
  cards: BattleHandCard[];
}

export type BattleActionKind = "skill" | "card" | "item" | "swap" | "pass" | "flee";

export interface BattleActionPayload {
  kind: BattleActionKind;
  /** skillId / cardInstanceId / itemRowId / benchCombatantId. */
  ref?: string;
  targetId?: string;
}

export type BattleActionErrorReason =
  | "phase_not_input"
  | "unknown_skill"
  | "skill_not_learned"
  | "not_enough_pp"
  | "card_not_in_hand"
  | "unknown_card"
  | "not_enough_mana"
  | "invalid_item"
  | "item_not_owned"
  | "bench_not_found"
  | "combatant_fainted"
  | "invalid_target"
  | "internal";

export interface BattleStrikeEvent {
  side: "player" | "enemy";
  strikes: number;
}

export interface BattleRewardEvent {
  bits: number;
  xp: number;
  loot: Array<{ kind: "item" | "card"; id: string; qty: number }>;
}

export interface BattleLevelUp {
  digimonId: string;
  from: number;
  to: number;
  skills: string[];
}

export interface BattleTamerLevelUp {
  oldLevel: number;
  newLevel: number;
}

export interface BattleEndEvent {
  victorySide: "player" | "enemy";
}

export interface PvpBattleStartEvent {
  side: "player" | "enemy";
}

// ---------- trade ----------

export type TradePhase = "waiting" | "negotiating" | "locked" | "completed" | "cancelled";

export interface TradeItemEntry {
  itemRowId: string;
  templateId: string;
  name: string;
  qty: number;
}

export interface TradeDigimonEntry {
  digimonId: string;
  speciesId: string;
  name: string;
  level: number;
  ivTotal: number;
}

export interface TradeOffer {
  characterId: string;
  characterName: string;
  confirmed: boolean;
  bits: number;
  crystals: number;
  items: TradeItemEntry[];
  digimons: TradeDigimonEntry[];
}

export interface TradeStateSchema {
  phase: TradePhase;
  errorMessage: string;
  offers: TradeOffer[];
  /** ms epoch — set when phase=locked. */
  confirmDeadline: number;
}

export type TradeErrorReason =
  | "phase_not_negotiating"
  | "soulbound"
  | "not_tradeable"
  | "insufficient_bits"
  | "item_locked"
  | "same_character"
  | "already_added"
  | "already_in_trade"
  | string;

export interface TradeCompleteEvent {
  tradeId: string;
  receipt: { aGave: unknown; aGot: unknown; bGave: unknown; bGot: unknown };
}

// ---------- WebSocket close codes ----------

export const CLOSE_CODE = {
  INVALID_TOKEN: 4000,
  KICKED_FROM_PARTY: 4001,
  MATCHMAKER_ERROR: 4213,
} as const;
